import type { ReactNode } from 'react';
import { Eye } from 'lucide-react';
import {
  Button,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/shared/components';
import { MobileEditorFooter, type MobileSaveState } from './MobileEditorFooter';
import type { Pane } from './useMessagesView';

interface Props {
  title: string;
  subtitle: string;
  editor: ReactNode;
  preview: ReactNode;
  pane: Pane;
  onPaneChange: (pane: Pane) => void;
  save: MobileSaveState;
  onJump: () => void;
}

/**
 * Auf dem Telefon: der gewählte Anlass im Vollbild, die Vorschau als zweite Ansicht. Kopf mit
 * Schließen und Fuß mit Speichern stehen fest, dazwischen scrollt nur der Inhalt — beide bleiben
 * damit auch auf 360 × 800 im Bild. Ungespeichertes geht beim Schließen nicht verloren.
 */
export function MobileMessageEditor(props: Props) {
  const previewing = props.pane === 'preview';
  return (
    <Sheet open onOpenChange={(open) => !open && props.save.onClose()}>
      <SheetContent>
        <SheetHeader className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <SheetTitle className="text-ink truncate font-serif text-lg">
              {previewing ? 'Vorschau' : props.title}
            </SheetTitle>
            <SheetDescription className="text-ink-soft truncate text-xs">
              {previewing
                ? `${props.title} · ${props.save.dirty ? 'Entwurf' : 'gespeichert'}`
                : props.subtitle}
            </SheetDescription>
          </div>
          {!previewing && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="border-line min-h-11 shrink-0 border"
              leftIcon={<Eye className="size-4" />}
              onClick={() => props.onPaneChange('preview')}
            >
              Vorschau
            </Button>
          )}
        </SheetHeader>
        <SheetBody className="bg-paper overflow-x-hidden">
          {previewing ? props.preview : props.editor}
        </SheetBody>
        <SheetFooter className="flex-nowrap">
          <MobileEditorFooter
            state={props.save}
            previewing={previewing}
            onBack={() => props.onPaneChange('edit')}
            onJump={props.onJump}
          />
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
